/**
 * Unified Identity Engine — Supabase Native Auth
 *
 * Single identity context for the entire Sentinel platform.
 * Uses Supabase Auth (email/password) as the identity provider.
 * The sentinel_users table stores roles, organization, and license metadata.
 *
 * One login, one identity, one session across Sentinel.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import type { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import type { UserRole } from "./certifications";

export interface GlobalIdentity {
  userId: string | null;
  authId: string | null;
  email: string | null;
  name: string | null;
  roles: UserRole[];
  organizationId: string | null;
  departmentId: string | null;
  activeLicenses: string[];
  completedCertifications: string[];
  sessionToken: string | null;
  isAuthenticated: boolean;
  organizationName: string | null;
  organizationLogoUrl: string | null;
  approvalStatus: string | null;
}

const EMPTY_IDENTITY: GlobalIdentity = {
  userId: null,
  authId: null,
  email: null,
  name: null,
  roles: [],
  organizationId: null,
  departmentId: null,
  activeLicenses: [],
  completedCertifications: [],
  sessionToken: null,
  isAuthenticated: false,
  organizationName: null,
  organizationLogoUrl: null,
  approvalStatus: null,
};

interface IdentityContextValue {
  identity: GlobalIdentity;
  setIdentity: (id: GlobalIdentity) => void;
  clearIdentity: () => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  loading: boolean;
}

const IdentityContext = createContext<IdentityContextValue | null>(null);

async function fetchSentinelUser(authId: string): Promise<Partial<GlobalIdentity> | null> {
  try {
    const { data, error } = await supabase
      .from("sentinel_users")
      .select(`
        id, name, role, organization_id, department_id,
        active_licenses, completed_certifications, approval_status,
        sentinel_organizations!inner ( id, name, logo_url )
      `)
      .eq("auth_id", authId)
      .maybeSingle();
    if (error || !data) return null;
    const org = Array.isArray(data.sentinel_organizations) ? data.sentinel_organizations[0] : data.sentinel_organizations;
    return {
      userId: data.id,
      roles: [data.role as UserRole],
      organizationId: data.organization_id ?? null,
      departmentId: data.department_id ?? null,
      activeLicenses: data.active_licenses ?? [],
      completedCertifications: data.completed_certifications ?? [],
      name: data.name,
      organizationName: org?.name ?? null,
      organizationLogoUrl: org?.logo_url ?? null,
      approvalStatus: data.approval_status ?? null,
    };
  } catch {
    return null;
  }
}

function authUserToIdentity(user: User, session: Session, extra?: Partial<GlobalIdentity> | null): GlobalIdentity {
  return {
    userId: extra?.userId ?? null,
    authId: user.id,
    email: user.email ?? null,
    name: extra?.name ?? user.user_metadata?.name ?? user.email?.split("@")[0] ?? null,
    roles: extra?.roles ?? (["student" as UserRole]),
    organizationId: extra?.organizationId ?? null,
    departmentId: extra?.departmentId ?? null,
    activeLicenses: extra?.activeLicenses ?? [],
    completedCertifications: extra?.completedCertifications ?? [],
    sessionToken: session.access_token,
    isAuthenticated: true,
    organizationName: extra?.organizationName ?? null,
    organizationLogoUrl: extra?.organizationLogoUrl ?? null,
    approvalStatus: extra?.approvalStatus ?? null,
  };
}

export function IdentityProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentityState] = useState<GlobalIdentity>(EMPTY_IDENTITY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      if (session?.user) {
        const extra = await fetchSentinelUser(session.user.id);
        setIdentityState(authUserToIdentity(session.user, session, extra));
      }
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      if (!mounted) return;
      if (event === "SIGNED_OUT" || !session) {
        setIdentityState(EMPTY_IDENTITY);
        return;
      }
      if (session.user) {
        const extra = await fetchSentinelUser(session.user.id);
        setIdentityState(authUserToIdentity(session.user, session, extra));
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const setIdentity = useCallback((id: GlobalIdentity) => {
    setIdentityState(id);
  }, []);

  const clearIdentity = useCallback(() => {
    setIdentityState(EMPTY_IDENTITY);
  }, []);

  const hasRole = useCallback(
    (role: UserRole) => identity.roles.includes(role),
    [identity.roles],
  );

  const hasAnyRole = useCallback(
    (roles: UserRole[]) => roles.some((r) => identity.roles.includes(r)),
    [identity.roles],
  );

  return (
    <IdentityContext.Provider
      value={{ identity, setIdentity, clearIdentity, hasRole, hasAnyRole, loading }}
    >
      {children}
    </IdentityContext.Provider>
  );
}

export function useIdentity(): IdentityContextValue {
  const ctx = useContext(IdentityContext);
  if (!ctx) {
    throw new Error("useIdentity must be used within an IdentityProvider");
  }
  return ctx;
}

export { EMPTY_IDENTITY };
